<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { useAdmin } from 'dashboard/composables/useAdmin';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  /* Quem foi o público deste disparo, escrito por quem disparou. Opcional:
     campanha antiga e quem não preencheu simplesmente não mostram o ícone. */
  audienceDescription: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  isDrip: {
    type: Boolean,
    default: false,
  },
  dripStatus: {
    type: String,
    default: null,
  },
  dripConfig: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'start',
  'pause',
  'resume',
  'cancel',
  'preview',
  'progress',
]);

const { t } = useI18n();
const { isAdmin } = useAdmin();

const DRIP_STATUS_I18N = {
  draft: 'CAMPAIGN.DRIP.STATUS.DRAFT',
  scheduled: 'CAMPAIGN.DRIP.STATUS.SCHEDULED',
  running: 'CAMPAIGN.DRIP.STATUS.RUNNING',
  paused: 'CAMPAIGN.DRIP.STATUS.PAUSED',
  completed: 'CAMPAIGN.DRIP.STATUS.COMPLETED',
  failed: 'CAMPAIGN.DRIP.STATUS.FAILED',
  cancelled: 'CAMPAIGN.DRIP.STATUS.CANCELLED',
};
const dripStatusLabel = computed(() =>
  t(DRIP_STATUS_I18N[props.dripStatus] || DRIP_STATUS_I18N.draft)
);
// 'scheduled' segue editável/iniciável como rascunho (o início automático só
// acontece quando o scheduler promove a campanha no horário marcado)
const isDripDraft = computed(
  () =>
    !props.dripStatus ||
    props.dripStatus === 'draft' ||
    props.dripStatus === 'scheduled'
);
const isDripScheduled = computed(() => props.dripStatus === 'scheduled');
const scheduledAtLabel = computed(() => {
  if (!props.scheduledAt) return '';
  const date = new Date(props.scheduledAt * 1000);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
});

// Auto-freio (anti-ban): quando o scheduler pausa por falha alta, grava o motivo
// no drip_config — mostra um alerta âmbar pro operador (achado de UX da revisão).
const brakeReason = computed(() =>
  props.dripStatus === 'paused' ? props.dripConfig?.brake_reason : null
);

const STATUS_COMPLETED = 'completed';
const STATUS_PROCESSING = 'processing';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

// Cor do badge do título p/ disparo em massa (drip): segue o estado real de execução.
const DRIP_BADGE_COLOR = {
  scheduled: 'text-n-blue-11',
  running: 'text-n-teal-11',
  paused: 'text-n-amber-11',
  failed: 'text-n-ruby-11',
  completed: 'text-n-slate-12',
  draft: 'text-n-slate-11',
};

const statusTextColor = computed(() => {
  if (props.isDrip) {
    return { [DRIP_BADGE_COLOR[props.dripStatus] || 'text-n-slate-11']: true };
  }
  return {
    'text-n-teal-11': isActive.value,
    'text-n-slate-12': !isActive.value,
  };
});

const campaignStatus = computed(() => {
  // Disparo em massa: o badge do título reflete o estado REAL de execução (drip_status)
  // — antes mostrava "Agendada" fixo (campaign_status=active) mesmo já rodando.
  if (props.isDrip) {
    return dripStatusLabel.value;
  }
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (props.status === STATUS_COMPLETED) {
    return t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED');
  }

  if (props.status === STATUS_PROCESSING) {
    return t('CAMPAIGN.SMS.CARD.STATUS.PROCESSING');
  }

  return t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout class="lg:[&>div]:flex-row lg:[&>div]:items-start">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1 w-full">
        <span class="text-base font-medium text-n-slate-12 break-words min-w-0">
          {{ title }}
        </span>
        <!-- Passe o mouse para ler para quem este disparo foi. Sem isto, o
             resultado da campanha é um número sem contexto: não dá para saber
             se um retorno baixo foi a mensagem ou a base escolhida. -->
        <span
          v-if="audienceDescription"
          v-tooltip="audienceDescription"
          class="inline-flex items-center self-center text-n-slate-10 hover:text-n-slate-12 cursor-help"
          :aria-label="`Público: ${audienceDescription}`"
          tabindex="0"
        >
          <Icon icon="i-lucide-users" class="size-4" />
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-2 break-words [&>p]:mb-0"
      />
      <div class="flex flex-wrap items-center w-full gap-x-2 gap-y-1">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
      <div
        v-if="isDripScheduled && scheduledAtLabel"
        class="text-xs text-n-blue-11"
      >
        {{ t('CAMPAIGN.DRIP.SCHEDULED_AT', { datetime: scheduledAtLabel }) }}
      </div>
      <!-- Auto-freio: alerta âmbar quando o disparo foi pausado por falha alta -->
      <div
        v-if="brakeReason"
        class="flex items-start w-full gap-1.5 text-xs text-n-amber-11"
      >
        <span class="i-ri-alert-line shrink-0 mt-0.5" />
        <span>{{
          t('CAMPAIGN.DRIP.AUTO_PAUSED', { reason: brakeReason })
        }}</span>
      </div>
    </div>
    <div
      class="flex flex-wrap items-center justify-start gap-2 lg:justify-end lg:max-w-sm lg:shrink-0"
    >
      <!-- Disparo em massa (drip): controles (o estado vai no badge do título). -->
      <template v-if="isDrip">
        <!-- Rascunho: prévia (estimativa antes de iniciar). Iniciada: andamento (ao vivo). -->
        <Button
          v-if="isDripDraft"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-ri-eye-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.PREVIEW')"
          @click="emit('preview')"
        />
        <Button
          v-else
          variant="faded"
          size="sm"
          color="slate"
          icon="i-ri-bar-chart-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.PROGRESS')"
          @click="emit('progress')"
        />
        <Button
          v-if="isDripDraft && isAdmin"
          variant="faded"
          size="sm"
          color="teal"
          icon="i-ri-play-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.START')"
          @click="emit('start')"
        />
        <Button
          v-else-if="dripStatus === 'running' && isAdmin"
          variant="faded"
          size="sm"
          color="amber"
          icon="i-ri-pause-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.PAUSE')"
          @click="emit('pause')"
        />
        <Button
          v-else-if="dripStatus === 'paused' && isAdmin"
          variant="faded"
          size="sm"
          color="teal"
          icon="i-ri-play-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.RESUME')"
          @click="emit('resume')"
        />
        <Button
          v-if="isDripDraft && isAdmin"
          variant="faded"
          size="sm"
          color="slate"
          icon="i-ri-equalizer-2-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.EDIT')"
          @click="emit('edit')"
        />
        <Button
          v-if="isDripScheduled && isAdmin"
          variant="faded"
          size="sm"
          color="ruby"
          icon="i-ri-forbid-line"
          :label="t('CAMPAIGN.DRIP.ACTIONS.CANCEL')"
          @click="emit('cancel')"
        />
      </template>
      <Button
        v-if="isLiveChatType && isAdmin"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-ri-equalizer-2-line"
        :aria-label="t('CAMPAIGN.LIVE_CHAT.EDIT.TITLE')"
        @click="emit('edit')"
      />
      <Button
        v-if="!isDripScheduled && isAdmin"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-ri-delete-bin-line"
        :aria-label="t('CAMPAIGN.CONFIRM_DELETE.CONFIRM')"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
